import React, { useState, useEffect } from 'react';
import { Button, Typography, Box, Paper } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import ResearchTheme from '../../styles/Theme.js';
import DownloadIcon from '@mui/icons-material/Download';
import { Divider } from '@mui/material';
import { api } from '../../services/api';
import ReactMarkdown from 'react-markdown';
import { useCallback } from 'react';


function Step4FinalReview({ userData, onComplete }) {

  const [loading, setLoading] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [responsesAnalysis, setResponsesAnalysis] = useState({});
  const [visibleSections, setVisibleSections] = useState({});
  const [visibleQuestions, setVisibleQuestions] = useState({});
  const [remainingAttempts, setRemainingAttempts] = useState(5);
  const [error, setError] = useState(null);

  const fetchSavedSurveyAnalysis = useCallback(async () => {
  try {
    const res = await api.getSavedAnalysis(userData.userID, userData.researchID);
    if (res && res.survey?.content?.sections) {
      setAnalysisResult(res.survey);
      res.survey.content.sections.forEach((_, index) => {
        setTimeout(() => {
          setVisibleSections(prev => ({ ...prev, [index]: true }));
        }, index * 500);
      });
    }
  } catch (err) {
    console.warn("No saved analysis found.");
  }
}, [userData]);

const fetchSavedQuestionAnalyses = useCallback(async () => {
  try {
    const result = await api.getSavedQuestionAnalyses(userData.userID, userData.researchID);
    setResponsesAnalysis(result || {});
    const visibleMap = {};
    Object.keys(result || {}).forEach(qid => {
      visibleMap[qid] = true;
    });
    setVisibleQuestions(visibleMap);
  } catch (err) {
    // console.warn("No saved question analyses found.");
  }
}, [userData]);

const fetchRemainingAttempts = useCallback(async () => {
  try {
    const result = await api.getRemainingAttempts(userData.userID, userData.researchID);
    setRemainingAttempts(result.remainingAttempts);
  } catch (err) {
    // console.warn("Could not fetch remaining attempts");
  }
}, [userData]);

  useEffect(() => {
    fetchSavedSurveyAnalysis();
    fetchSavedQuestionAnalyses();
    fetchRemainingAttempts();
  }, [fetchRemainingAttempts, fetchSavedQuestionAnalyses, fetchSavedSurveyAnalysis, userData]);

  // const handleSubmit = async () => {
  //   try {
  //     const response = await axios.post('/api/v1/final-review/', {
  //       userID: userData.userID,
  //       researchID: userData.researchID,
  //       // Include other necessary data
  //     });
  //     if (response.data.success) {
  //       onComplete();
  //     }
  //   } catch (error) {
  //     // console.error('Error finalizing review:', error);
  //   }
  // };

  const convertToCSV = (data) => {
    if (!data || data.length === 0) return '';
    
    const headers = Object.keys(data[0]);
    const csvRows = [headers.join(',')];
    
    data.forEach(row => {
      const values = headers.map(header => {
        const value = row[header];
        if (Array.isArray(value)) {
          return `"${value.join('; ')}"`;
        } else if (typeof value === 'object' && value !== null) {
          return `"${JSON.stringify(value).replace(/"/g, '""')}"`;
        } else if (typeof value === 'string') {
          return `"${value.replace(/"/g, '""')}"`;
        } else {
          return value;
        }
      });
      csvRows.push(values.join(','));
    });
    
    return csvRows.join('\n');
  };

  const downloadCSV = (data, filename) => {
    const csvContent = convertToCSV(data);
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };
  
  const handleDownloadResponses = () => {
    // Create a JSON object with the research data
    const researchData = {
      researchID: userData.researchID,
      description: userData.description,
      title: userData.title,
      surveyDescription: userData.surveyDescription,
      questions: userData.surveyQuestions,
      personas: userData.selectedPersonas,
      responses: userData.surveyResponses
    };
    
    // Convert the data to a JSON string
    const jsonData = JSON.stringify(researchData, null, 2);
    
    // Create a blob with the JSON data
    const blob = new Blob([jsonData], { type: 'application/json' });
    
    // Create a URL for the blob
    const url = URL.createObjectURL(blob);
    
    // Create a temporary link element
    const link = document.createElement('a');
    link.href = url;
    link.download = `research_${userData.researchID}.json`;
    
    // Append the link to the body
    document.body.appendChild(link);
    
    // Click the link to trigger the download
    link.click();
    
    // Clean up
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleDownloadPersonasCSV = () => {
    if (!userData.selectedPersonas) return;
    
    const personasArray = Object.entries(userData.selectedPersonas).map(([id, persona]) => ({
      personaID: id,
      ...persona
    }));
    downloadCSV(personasArray, `personas_${userData.researchID}.csv`);
  };

  const handleDownloadSurveyCSV = () => {
    if (!userData.surveyQuestions) return;
    
    const questionsArray = userData.surveyQuestions.map(question => ({
      questionID: question.questionID,
      questionText: question.questionText,
      questionType: question.questionType,
      responseOptions: Array.isArray(question.responseOptions) ? question.responseOptions.join('; ') : '',
      selectMin: question.selectMin,
      selectMax: question.selectMax
    }));
    downloadCSV(questionsArray, `survey_${userData.researchID}.csv`);
  };

  const handleDownloadResponsesCSV = () => {
    if (!userData.surveyResponses) return;
    
    // Transform responses to a more CSV-friendly format
    const flattenedResponses = [];
    
    userData.surveyResponses.forEach(response => {
      flattenedResponses.push({
        personaID: response.personaID,
        questionID: response.questionID,
        response: Array.isArray(response.response) ? response.response.join('; ') : response.response,
        researchID: response.researchID
      });
    });
    
    downloadCSV(flattenedResponses, `responses_${userData.researchID}.csv`);
  };

  const handleGenerateAllAnalyses = async () => {
    try {
      setLoading(true);
      setError(null);
      setVisibleSections({});
      setVisibleQuestions({});

      // Prepare questions data for analysis
      const questions = userData.surveyQuestions.map(question => {
        const responses = userData.surveyResponses.filter(r => r.questionID === question.questionID);
        const personas = userData.selectedPersonas;
        
        // Define the attributes to group by
        const attributes = ["income", "gender", "age_range", "employment_status", "household_situation", "education", "marital_status"];
        
        // Calculate total counts
        const totalCounts = {};
        responses.forEach(({ response }) => {
          const values = Array.isArray(response) ? response : [response];
          values.forEach(v => {
            totalCounts[v] = (totalCounts[v] || 0) + 1;
          });
        });

        // Initialize attribute groups
        const attributeGroups = {};
        attributes.forEach(attr => {
          attributeGroups[attr] = {};
        });

        // Calculate grouped data for each attribute
        responses.forEach(({ response, personaID }) => {
          const persona = personas[personaID];
          const values = Array.isArray(response) ? response : [response];
          
          values.forEach(v => {
            attributes.forEach(attr => {
              const attrValue = persona?.[attr] || 'Unknown';
              
              if (!attributeGroups[attr][v]) {
                attributeGroups[attr][v] = {};
              }
              
              attributeGroups[attr][v][attrValue] = (attributeGroups[attr][v][attrValue] || 0) + 1;
            });
          });
        });

        // Convert grouped data to array format
        const flatGrouped = [];
        Object.entries(attributeGroups).forEach(([attribute, groups]) => {
          Object.entries(groups).forEach(([option, attrCounts]) => {
            flatGrouped.push({
              option,
              attribute,
              ...attrCounts
            });
          });
        });
        
        return {
          questionID: question.questionID,
          totalCounts,
          groupedData: flatGrouped
        };
      });

      // Start the full analysis
      await api.generateFullAnalysis(
        userData.userID,
        userData.researchID,
        userData.description,
        questions
      );

      // Wait for completion
      const result = await api.waitForAnalysisCompletion(
        userData.userID,
        userData.researchID
      );

      // Handle the results
      if (result.survey) {
        setAnalysisResult(result.survey);
        result.survey.content?.sections?.forEach((_, index) => {
          setTimeout(() => {
            setVisibleSections(prev => ({ ...prev, [index]: true }));
          }, index * 500);
        });
      }

      if (result.questions) {
        setResponsesAnalysis(result.questions);
        const visibleMap = {};
        Object.keys(result.questions).forEach(qid => {
          visibleMap[qid] = true;
        });
        setVisibleQuestions(visibleMap);
      }

    } catch (error) {
      // console.error("Analysis generation failed", error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateSurveyAnalysis = async (regenerate = false) => {
    try {
      if (regenerate) {
        if (remainingAttempts <= 0) {
          alert("You've reached the maximum number of regenerate attempts.");
          return;
        }
        await api.decrementAttempt(userData.userID, userData.researchID);
        setRemainingAttempts(prev => Math.max(prev - 1, 0));
        
        setLoading(true);
        setError(null);

        // Generate only survey analysis
        await api.generateAnalysis(
          userData.userID,
          userData.researchID,
          userData.description
        );

        // Wait for completion using the same mechanism as first generation
        const result = await api.waitForAnalysisCompletion(
          userData.userID,
          userData.researchID
        );

        // Handle the results
        if (result.survey) {
          setAnalysisResult(result.survey);
          result.survey.content?.sections?.forEach((_, index) => {
            setTimeout(() => {
              setVisibleSections(prev => ({ ...prev, [index]: true }));
            }, index * 500);
          });
        }

        setLoading(false);
      } else {
        await handleGenerateAllAnalyses();
      }
    } catch (error) {
      // console.error("Survey analysis failed", error);
      setError(error.message);
      setLoading(false);
    }
  };

  const handleGenerateResponseAnalysis = async (questionID, regenerate = false) => {
    try {
      if (regenerate) {
        if (remainingAttempts <= 0) {
          alert("You've reached the maximum number of regenerate attempts.");
          return;
        }
        await api.decrementAttempt(userData.userID, userData.researchID);
        setRemainingAttempts(prev => Math.max(prev - 1, 0));
        
        setLoading(true);
        setError(null);

        const responses = userData.surveyResponses.filter(r => r.questionID === questionID);
        const personas = userData.selectedPersonas;

        // Define the attributes to group by
        const attributes = ["income", "gender", "age_range", "employment_status", "household_situation", "education", "marital_status"];

        // Calculate total counts
        const totalCounts = {};
        responses.forEach(({ response }) => {
          const values = Array.isArray(response) ? response : [response];
          values.forEach(v => {
            totalCounts[v] = (totalCounts[v] || 0) + 1;
          });
        });

        // Initialize attribute groups
        const attributeGroups = {};
        attributes.forEach(attr => {
          attributeGroups[attr] = {};
        });

        // Calculate grouped data for each attribute
        responses.forEach(({ response, personaID }) => {
          const persona = personas[personaID];
          const values = Array.isArray(response) ? response : [response];
          
          values.forEach(v => {
            attributes.forEach(attr => {
              const attrValue = persona?.[attr] || 'Unknown';
              
              if (!attributeGroups[attr][v]) {
                attributeGroups[attr][v] = {};
              }
              
              attributeGroups[attr][v][attrValue] = (attributeGroups[attr][v][attrValue] || 0) + 1;
            });
          });
        });

        // Convert grouped data to array format
        const flatGrouped = [];
        Object.entries(attributeGroups).forEach(([attribute, groups]) => {
          Object.entries(groups).forEach(([option, attrCounts]) => {
            flatGrouped.push({
              option,
              attribute,
              ...attrCounts
            });
          });
        });

        // Generate only response analysis
        await api.generateResponsesAnalysis(
          userData.userID,
          userData.researchID,
          userData.description,
          totalCounts,
          flatGrouped,
          questionID
        );

        // Wait for completion using the same mechanism as first generation
        const result = await api.waitForAnalysisCompletion(
          userData.userID,
          userData.researchID
        );

        // Handle the results
        if (result.questions && result.questions[questionID]) {
          setResponsesAnalysis(prev => ({
            ...prev,
            [questionID]: result.questions[questionID]
          }));
          setTimeout(() => {
            setVisibleQuestions(prev => ({ ...prev, [questionID]: true }));
          }, 100);
        }

        setLoading(false);
      } else {
        await handleGenerateAllAnalyses();
      }
    } catch (error) {
      // console.error("Response analysis failed", error);
      setError(error.message);
      setLoading(false);
    }
  };

  // Remove ProgressBar component

  // Keep ErrorMessage component
  const ErrorMessage = () => {
    if (!error) return null;

    return (
      <Typography color="error" sx={{ mb: 2 }}>
        Error: {error}
      </Typography>
    );
  };

  return (
    <ThemeProvider theme={ResearchTheme}>
      <Box sx={{ width: '100%', p: 3 }}>
        <ErrorMessage />
        <Typography variant="h5" gutterBottom>
          Final Review
        </Typography>
        
        <Paper sx={{ p: 3, mb: 3 }}>
          
          <Typography variant="h6" gutterBottom>
            Research Summary
          </Typography>
          <Typography variant="body1" paragraph>
            Your research "{userData.title}" is complete. You have collected responses from {Object.keys(userData.selectedPersonas || {}).length} personas for {userData.surveyQuestions?.length || 0} questions.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, mt: 3, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadResponses}
            >
              Download All (JSON)
            </Button>

            <Button
              variant="outlined"
              color="primary"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadPersonasCSV}
            >
              Download Personas (CSV)
            </Button>

            <Button
              variant="outlined"
              color="primary"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadSurveyCSV}
            >
              Download Survey (CSV)
            </Button>

            <Button
              variant="outlined"
              color="primary"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadResponsesCSV}
            >
              Download Responses (CSV)
            </Button>
          </Box>
        </Paper>

        {!analysisResult && Object.keys(responsesAnalysis).length === 0 && (
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleGenerateAllAnalyses}
                disabled={loading}
              >
                {loading ? 'Generating Full Analysis...' : 'Generate All Analyses'}
              </Button>
            </Box>
          )}

        {analysisResult && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>Survey-Level Analysis</Typography>
          <Divider sx={{ my: 2 }} />

            <Box sx={{ mt: 2 }}>
              {analysisResult.content.sections.map((section, i) => (
                <Box 
                  key={i} 
                  sx={{ 
                    pr: 3,
                    pl: 3,
                    pt: 3,
                    opacity: visibleSections[i] ? 1 : 0,
                    transform: visibleSections[i] ? 'translateY(0)' : 'translateY(10px)',
                    transition: 'opacity 0.5s ease, transform 0.5s ease',
                  }}
                >
                  <Typography variant="h6" sx={{ 
                    fontWeight: 600, 
                    mb: 3, 
                    backgroundColor: 'var(--primary-color)', 
                    borderRadius: '8px',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.6)',
                    position: 'relative',
                    overflow: 'hidden',
                    padding: '8px 16px',
                    width: 'fit-content',
                    color: 'var(--text-color)'
                    }}>
                    {section.heading}
                  </Typography>

                  {section.paragraphs.map((para, pIdx) => (
                    <ReactMarkdown
                      components={{
                        p: ({ node, ...props }) => (
                          <Typography variant="body1" sx={{ fontSize: '1.05rem', lineHeight: 1.6, pl: 2, mb:2 }} {...props} />
                        ),
                        strong: ({ node, ...props }) => (
                          <strong style={{ fontWeight: 'bold' }} {...props} />
                        )
                      }}
                    >
                      {para}
                    </ReactMarkdown>
                  ))}

                  {section.bullets.length > 0 && (
                    <ul style={{ marginLeft: '1rem' }}>
                      {section.bullets.map((bullet, bIdx) => (
                        <li key={bIdx}>
                          <ReactMarkdown
                            components={{
                              p: ({ node, ...props }) => <span {...props} />,
                              strong: ({ node, ...props }) => (
                                <strong style={{ fontWeight: 'bold' }} {...props} />
                              ),
                            }}
                          >
                            {bullet}
                          </ReactMarkdown>
                        </li>
                      ))}
                    </ul>
                  )}

                  {section.subsections.map((sub, sIdx) => (
                    <Box key={sIdx} sx={{ 
                    fontWeight: 500, 
                    mb: 2,
                    mr: 1,
                    ml: 1, 
                    backgroundColor: 'var(--secondary-color)', 
                    borderRadius: '8px',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
                    position: 'relative',
                    overflow: 'hidden',
                    padding: '8px 16px',
                    
                    }}>
                      <Typography variant="h6" sx={{ fontWeight: 'bold', pl: 1, pr: 1, pb: 2, pt: 2}}>
                        {sub.subheading}
                      </Typography>

                      <Divider sx={{ mb: 2, ml:1, mr:1,  backgroundColor: 'var(--tertiary-color)', borderWidth: 1.2 }} />

                      {sub.paragraphs.map((subPara, spIdx) => (
                        <ReactMarkdown
                          components={{
                            p: ({ node, ...props }) => (
                              <Typography variant="body1" sx={{ fontSize: '1.05rem',
                                lineHeight: 1.6, pl: 2, mb:2 }} {...props} />
                            ),
                            strong: ({ node, ...props }) => (
                              <strong style={{ fontWeight: 'bold' }} {...props} />
                            )
                          }}
                        >
                          {subPara}
                        </ReactMarkdown>
                      ))}

                      {sub.bullets.length > 0 && (
                        <ul style={{ marginLeft: '1rem' }}>
                          {sub.bullets.map((bullet, sbIdx) => (
                            <li key={sbIdx}>
                              <ReactMarkdown
                                components={{
                                  p: ({ node, ...props }) => <span {...props} />,
                                  strong: ({ node, ...props }) => (
                                    <strong style={{ fontWeight: 'bold' }} {...props} />
                                  ),
                                }}
                              >
                                {bullet}
                              </ReactMarkdown>
                            </li>
                          ))}

                        </ul>
                      )}
                    </Box>
                  ))}
                </Box>
              ))}
            </Box>
          
          <Box sx={{ textAlign: 'center', mb: 6, mt: 4 }}>
            <Button 
              variant="contained"
              onClick={() => handleGenerateSurveyAnalysis(true)}
              disabled={loading || remainingAttempts <= 0}
            >
              {loading 
                ? 'Generating...' 
                : `Regenerate Survey Analysis (${remainingAttempts} attempts left)`
              }
            </Button>
          </Box>
        </Paper>
        )}

        {Object.keys(responsesAnalysis).length > 0 && (
        <Paper sx={{ p: 3, mb: 10 }}>
          <Typography variant="h6" gutterBottom>
            Individual Question-Level Analysis
          </Typography>
          <Divider sx={{ my: 2 }} />

          {(userData?.surveyQuestions || []).map((q, index) => {
            if (q.questionType !== 'multiple-choice') return null;

            return (
              <Box
                key={q.questionID}
                sx={{
                  mb: 4,
                  p: 3,
                  borderRadius: 2,
                  backgroundColor: 'var(--primary-color)',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
                  opacity: visibleQuestions[q.questionID] ? 1 : 0,
                  transform: visibleQuestions[q.questionID] ? 'translateY(0)' : 'translateY(10px)',
                  transition: 'opacity 0.5s ease, transform 0.5s ease',
                  pointerEvents: visibleQuestions[q.questionID] ? 'auto' : 'none',
                  height: visibleQuestions[q.questionID] ? 'auto' : 0,
                  overflow: 'hidden',
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    mb: 2,
                    backgroundColor: 'var(--primary-color)',
                    borderRadius: '8px',
                    padding: '8px 16px',
                    color: 'var(--text-color)',
                    width: 'fit-content',
                  }}
                >
                  Q{index + 1}: {q.questionText}
                </Typography>

                <Box sx={{ mt: 2 }}>
                  {responsesAnalysis[q.questionID]?.analysis_text ? (
                    
                    <ReactMarkdown
                      components={{
                        p: ({ node, ...props }) => (
                          <Typography variant="body1" sx={{ fontSize: '1.05rem', lineHeight: 1.6, pl: 2, mb:2 }} {...props} />
                        ),
                        strong: ({ node, ...props }) => (
                          <strong style={{ fontWeight: 'bold' }} {...props} />
                        )
                      }}
                    >
                      {responsesAnalysis[q.questionID].analysis_text}
                    </ReactMarkdown>
                  ) : (
                    <Typography variant="body2" sx={{ fontStyle: 'italic', mb: 2, pl: 2 }}>
                      No analysis generated yet.
                    </Typography>
                  )}

                  <Box sx={{ textAlign: 'center', mt: 3 }}>
                    <Button
                      variant="contained"
                      onClick={() => handleGenerateResponseAnalysis(q.questionID, true)}
                      disabled={loading || remainingAttempts <= 0}
                     
                    >
                      {loading
                        ? 'Generating...'
                        : `Regenerate Analysis (${remainingAttempts} attempts left)`
                      }
                    </Button>
                  </Box>
                </Box>
              </Box>
              
            );
          })}
        </Paper>
        )}
      </Box>
    </ThemeProvider>
  );
}

export default Step4FinalReview;